import { useEffect, useState } from 'react';
import { useQueryClient, type QueryKey } from '@tanstack/react-query';
import { api } from '@/lib/api';
import { ticketKeys } from '@/queries/keys';

export type TicketStreamEvent = 'message' | 'ticket' | 'agent-activity' | 'agent-item' | 'refetch';

const STREAM_EVENTS: TicketStreamEvent[] = ['message', 'ticket', 'agent-activity', 'agent-item', 'refetch'];

const RETRY_MIN_MS = 1_000;
const RETRY_MAX_MS = 60_000;

// The queries each event makes stale. Events carry ids only, never content: the refetch goes through the normal,
// access-checked endpoints, so a customer's page can't learn more from the stream than it could by reloading. The
// keys are prefixes, so every view-as variant of a ticket's queries is covered.
export function keysForEvent(event: TicketStreamEvent, ticketId: string): QueryKey[] {
  switch (event) {
    case 'message':
    case 'ticket':
      return [ticketKeys.detail(ticketId), ticketKeys.lists];
    case 'agent-activity':
      return [ticketKeys.agentActivity(ticketId)];
    case 'agent-item':
      return [ticketKeys.agentItems(ticketId)];
    case 'refetch':
      return [ticketKeys.detail(ticketId), ticketKeys.agentActivity(ticketId), ticketKeys.agentItems(ticketId), ticketKeys.lists];
  }
}

function errorStatus(err: unknown): number | undefined {
  const e = err as { status?: number; statusCode?: number } | null;
  return e?.status ?? e?.statusCode;
}

// Live updates for one ticket over server-sent events. Returns `live` while the server's change stream is up, so
// the caller can pause its polling; anything else (connecting, a dropped connection, an API without a replica set)
// leaves `live` false and polling on.
export function useTicketEvents(ticketId: string, asUser?: string): { live: boolean } {
  const qc = useQueryClient();
  const [live, setLive] = useState(false);

  useEffect(() => {
    if (!ticketId || typeof EventSource === 'undefined') return;

    let stopped = false;
    let source: EventSource | null = null;
    let timer: ReturnType<typeof setTimeout> | undefined;
    let attempt = 0;
    let lastEventId: string | undefined;

    const scheduleReconnect = () => {
      setLive(false);
      if (stopped) return;
      const ceiling = Math.min(RETRY_MAX_MS, RETRY_MIN_MS * 2 ** attempt);
      attempt += 1;
      // Jittered, so every page open on a restarting API task doesn't come back in the same instant.
      timer = setTimeout(connect, ceiling / 2 + Math.random() * (ceiling / 2));
    };

    async function connect() {
      let token: string;
      try {
        // EventSource can't send the Authorization header, so the stream takes a short-lived token minted here
        // rather than the Auth0 JWT, which must never sit in a URL.
        ({ token } = await api<{ token: string }>(`/support-tickets/${ticketId}/events/token`, {
          method: 'POST',
          query: asUser ? { asUserId: asUser } : undefined
        }));
      } catch (err) {
        const status = errorStatus(err);
        // Out of reach: the page's own query reports that; retrying would only repeat it.
        if (status === 403 || status === 404) return;
        scheduleReconnect();
        return;
      }
      if (stopped) return;

      const params = new URLSearchParams({ token });
      if (lastEventId) params.set('lastEventId', lastEventId);
      const base = String(import.meta.env.VITE_API_URL ?? '').replace(/\/$/, '');
      const es = new EventSource(`${base}/support-tickets/${encodeURIComponent(ticketId)}/events?${params}`);
      source = es;

      es.addEventListener('status', e => {
        const message = e as MessageEvent<string>;
        lastEventId = message.lastEventId || lastEventId;
        const isLive = Boolean((JSON.parse(message.data) as { live?: boolean }).live);
        if (isLive) attempt = 0;
        setLive(isLive);
      });
      STREAM_EVENTS.forEach(name =>
        es.addEventListener(name, e => {
          lastEventId = (e as MessageEvent<string>).lastEventId || lastEventId;
          keysForEvent(name, ticketId).forEach(queryKey => qc.invalidateQueries({ queryKey }));
        })
      );
      // Left alone, EventSource reconnects with the same URL, and so the same spent token. Close it and come back
      // with a fresh one instead; the server answers a reconnect's lastEventId with a refetch.
      es.onerror = () => {
        es.close();
        if (source === es) source = null;
        scheduleReconnect();
      };
    }

    void connect();
    return () => {
      stopped = true;
      clearTimeout(timer);
      source?.close();
      setLive(false);
    };
  }, [ticketId, asUser, qc]);

  return { live };
}
